import {FC, useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {constant, toUpper} from 'lodash'
import {useHistory} from 'react-router-dom'
import {ApolloClient, gql, useApolloClient} from '@apollo/client'
import ActionButton from 'jbc-front/components/ActionButton'
import {useSelector} from 'hooks/redux'
import {useNotify} from 'hooks/useNotify'
import {isAdminSelector, isLimitedAdminSelector} from 'libs/redux'
import {asyncError} from 'store/actions/asyncError'
import {PreviewPdfModal, PreviewPdfData} from './PreviewPdfModal'

// @ts-expect-error
import {MyNumber} from 'jbc-front/components/icons'
// @ts-ignore
import api from 'api'

// マイナンバーを利用する帳票のリスト
const useMyNumberReports = [
  'r2',
  'r8',
  'r13',
  'r57',
  'r58',
  'r501',
  'r502',
  'r503',
  'r504',
  'r505',
  'r506',
  'r509',
  'r512',
  'r513',
]
// マイナンバーの利用可否を編集画面で選択する帳票のリスト（その他健保以外）
const usableMyNumberReports = ['r1', 'r4', 'r6', 'r7', 'r9', 'r507', 'r508']
// マイナンバーの利用可否をGraphQL経由で確認する帳票のリスト
const checkViaGraphQLReports = ['r9', 'r507', 'r508']

interface IsUseMyNumberProps {
  procedureType: string
  healthInsuranceType?: string
  isUseMyNumber: boolean
}
const isUseMyNumberByReport: Record<string, (payload: IsUseMyNumberProps) => boolean> = {
  r1: (payload) => payload.isUseMyNumber,
  r2: constant(true),
  r4: (payload) => payload.isUseMyNumber,
  r5: (payload) => ['enroll', 'add_dependents'].includes(payload.procedureType),
  r6: (payload) => (payload.healthInsuranceType !== 'other' ? payload.isUseMyNumber : true),
  r7: (payload) => payload.isUseMyNumber,
  r8: constant(true),
  r9: (payload) => payload.isUseMyNumber,
  r13: constant(true),
  r57: constant(true),
  r58: constant(true),
  r501: constant(true),
  r502: constant(true),
  r503: constant(true),
  r504: constant(true),
  r505: constant(true),
  r506: constant(true),
  r507: (payload) => payload.isUseMyNumber,
  r508: (payload) => payload.isUseMyNumber,
  r509: constant(true),
  r512: constant(true),
  r513: constant(true),
}

const checkQueries = new Map(
  checkViaGraphQLReports.map((reportNum) => [
    reportNum,
    gql`
      query CheckUseMyNumberInReport_${reportNum}($procedureStatusId: ID!) {
        client {
          id
          procedureStatus(procedureStatusId: $procedureStatusId) {
            id
            ${reportNum}ParamSet {
              id
              isUseMyNumber
            }
          }
        }
      }
    `,
  ])
)

const checkUseMyNumberInReport =
  <TCacheShape,>(apolloClient: ApolloClient<TCacheShape>) =>
  async (procedureStatusId: string, reportNum: string) => {
    const query = checkQueries.get(reportNum)
    if (!query) {
      throw new Error('invalid report num')
    }

    const {data} = await apolloClient.query({
      query,
      variables: {procedureStatusId},
      fetchPolicy: 'no-cache',
    })
    return !!data?.client?.procedureStatus?.[`${reportNum}ParamSet`]?.isUseMyNumber
  }

export interface PreviewProcedurePdfData extends PreviewPdfData {
  num: string // 帳票コード (e.g. 'r1', 'r14', 'r14Other')
  procedureStatusId: string | number
  dependentId?: string | number
}

interface PreviewProcedurePdfModalProps {
  data?: PreviewProcedurePdfData
  procedureStatus: {
    procedureType: string
    employeeId: string | number
    healthInsuranceType?: string
    isRelatedRecordsDirty: boolean
  }
  onClose: () => void
}

export const PreviewProcedurePdfModal: FC<PreviewProcedurePdfModalProps> = ({data, procedureStatus, ...props}) => {
  const dispatch = useDispatch()
  const notify = useNotify()
  const history = useHistory()
  const token = useSelector((state) => state.auth.token)
  const apolloClient = useApolloClient()
  const isAdmin = useSelector(isAdminSelector)
  const isLimitedAdmin = useSelector(isLimitedAdminSelector)
  const [isUseMyNumber, setIsUseMyNumber] = useState(false)
  const [isVisibleMyNumber, setIsVisibleMyNumber] = useState(false)
  const [pdfWithMyNumber, setPdfWithMyNumber] = useState<string>('')

  // usableMyNumberReportsに含まれる帳票が入力された場合、マイナンバー利用可否をチェック
  useEffect(() => {
    const procedureStatusId = data?.procedureStatusId
    const reportNum = data?.num
    if (!procedureStatusId || !reportNum || !usableMyNumberReports.includes(reportNum)) {
      setIsUseMyNumber(false)
      return
    }

    ;(async () => {
      try {
        if (checkViaGraphQLReports.includes(reportNum)) {
          setIsUseMyNumber(await checkUseMyNumberInReport(apolloClient)(`${procedureStatusId}`, reportNum))
        } else {
          const report: Record<string, Record<string, unknown>> = await api
            .createWithAuth(token)
            .procedureStatuses.reports.get(procedureStatusId, reportNum, {
              employee_dependent_id: data?.dependentId,
            })
          setIsUseMyNumber(!!report[reportNum]?.['is_use_my_number'])
        }
      } catch (err) {
        dispatch(asyncError(err))
      }
    })()
  }, [dispatch, token, apolloClient, data?.procedureStatusId, data?.num, data?.dependentId])

  // 入力された帳票が変わったら状態リセット
  useEffect(() => {
    setIsUseMyNumber(false)
    setIsVisibleMyNumber(false)
    setPdfWithMyNumber('')
  }, [data?.url])

  const handleMyNumberClick = async () => {
    // 表示から非表示に切り替えた時の処理
    if (isVisibleMyNumber) {
      setIsVisibleMyNumber(false)
      setPdfWithMyNumber('')
      return
    }

    // ここから、非表示から表示に切り替えた時の処理
    const reportNum = data?.num
    if (!reportNum) {
      return
    }

    if (procedureStatus.isRelatedRecordsDirty) {
      notify('編集内容はまだ反映されていません。「帳票に反映する」を押してください。', 'error')
      return
    }
    if (!isAdmin) {
      notify('マイナンバーの閲覧権限がありません', 'error')
      return
    }

    const getPdfAsync = async () => {
      const params = new URLSearchParams({
        type: `${data.num}`,
        is_visible_my_number: 'true',
      })
      if (data?.dependentId) {
        params.append('employee_dependent_id', `${data.dependentId}`)
      }
      const pdf = `/api/procedure_statuses/${data.procedureStatusId}/pdf?${params}`
      setPdfWithMyNumber(pdf)
      setIsVisibleMyNumber(true)
    }

    try {
      if (reportNum === 'r5' && ['enroll', 'add_dependents'].includes(procedureStatus.procedureType)) {
        // 帳票R5の場合は対象の被扶養者がマイナンバーを持っているかチェックして、持っていなければエラーメッセージを出す
        const response = await api
          .createWithAuth(token)
          .procedureStatuses.get(data.procedureStatusId, [
            'employee',
            'employee_dependents',
            'procedure_add_dependents_data',
            'my_number_present',
          ])
        const dependents =
          procedureStatus.procedureType === 'add_dependents'
            ? response.data.employee.employee_dependents.filter(({id}: {id: number}) =>
                response.data.procedure_add_dependents_data.find(
                  ({employee_dependent_id}: {employee_dependent_id: number}) => id === employee_dependent_id
                )
              )
            : response.data.employee.employee_dependents
        if (dependents?.some(({my_number_present}: {my_number_present: boolean}) => my_number_present)) {
          await getPdfAsync()
        } else {
          notify('被扶養者のマイナンバーを入力してください', 'error')
        }
      } else if (useMyNumberReports.includes(reportNum) || usableMyNumberReports.includes(reportNum)) {
        // R5以外のマイナンバー対応帳票の場合は被保険者か被扶養者がマイナンバーを持っているかチェックして、持っていなければエラーメッセージを出す
        const response = await api
          .createWithAuth(token)
          .employees.get(procedureStatus.employeeId, ['employee_dependents', 'my_number_present'])
        const dependents = response.data.employee_dependents
        if (
          response.data.my_number_present ||
          dependents?.some(({my_number_present}: {my_number_present: boolean}) => my_number_present)
        ) {
          await getPdfAsync()
        } else {
          notify('従業員のマイナンバーを入力してください', 'error')
        }
      } else {
        await getPdfAsync()
      }
    } catch (err) {
      dispatch(asyncError(err))
    }
  }

  const myNumberVisibility = (data?.num && isUseMyNumberByReport[data.num]) || (() => false)
  const myNumberButton =
    !isLimitedAdmin &&
    myNumberVisibility({
      procedureType: procedureStatus.procedureType,
      healthInsuranceType: procedureStatus.healthInsuranceType,
      isUseMyNumber,
    }) ? (
      <ActionButton onClick={handleMyNumberClick} icon={<MyNumber size={15} />}>
        {`マイナンバー${isVisibleMyNumber ? 'を隠す' : '出力'}`}
      </ActionButton>
    ) : null

  const handleEditClick = () => {
    if (!data) return

    let editUrl = `/procedure_statuses/${data.procedureStatusId}/report_edit`
    if (data.dependentId) {
      editUrl += `?dependent_id=${data.dependentId}`
    }
    let num = data.num
    if (num === 'r14Other') {
      num = 'r14'
    }
    editUrl += `#${toUpper(num)}`
    history.push(editUrl)
  }

  return (
    <PreviewPdfModal
      data={data && {...data, pdf: isVisibleMyNumber ? pdfWithMyNumber : data.pdf}}
      extraButtons={myNumberButton}
      onEditClick={handleEditClick}
      isVisibleMyNumber={isVisibleMyNumber}
      {...props}
    />
  )
}
